/**
 * Stripe Embedded Checkout wrapper
 *
 * Lazy-loads @stripe/stripe-js (~40KB) only when this component mounts.
 * Uses EmbeddedCheckoutProvider + EmbeddedCheckout from @stripe/react-stripe-js.
 */

import { useState, useCallback, lazy, Suspense } from 'react'
import { useMutation } from '@tanstack/react-query'
import { api } from '@/services/api'
import { useCurrentOrgId } from '@/hooks/useCurrentOrgId'
import { Button } from '@/components/ui'

const StripeCheckout = lazy(() => import('./StripeCheckoutInner'))

type CheckoutEmbedProps = {
  onComplete: () => void
}

export function CheckoutEmbed({ onComplete }: CheckoutEmbedProps) {
  const { orgId } = useCurrentOrgId()
  const [sessionData, setSessionData] = useState<{
    clientSecret: string
    publishableKey: string
  } | null>(null)

  const createSession = useMutation({
    mutationFn: () =>
      api.createCheckoutSession(orgId ?? '', {
        planType: 'base',
        successUrl: `${window.location.origin}/billing?success=true`,
        cancelUrl: `${window.location.origin}/billing?canceled=true`,
      }),
    onSuccess: (data) => {
      setSessionData(data)
    },
  })

  const handleStart = useCallback(() => {
    createSession.mutate()
  }, [createSession])

  if (sessionData) {
    return (
      <div className="card">
        <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
          <h3 className="text-lg font-medium leading-6 text-gray-900">
            Complete Your Subscription
          </h3>
        </div>
        <div className="p-0">
          <Suspense fallback={<CheckoutSkeleton />}>
            <StripeCheckout
              clientSecret={sessionData.clientSecret}
              publishableKey={sessionData.publishableKey}
              onComplete={onComplete}
            />
          </Suspense>
        </div>
      </div>
    )
  }

  return (
    <div className="card">
      <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
        <h3 className="text-lg font-medium leading-6 text-gray-900">
          Subscribe
        </h3>
      </div>
      <div className="px-4 py-5 sm:p-6">
        <p className="text-sm text-gray-600 mb-4">
          Start your subscription to unlock all features and keep your canaries active.
        </p>
        <Button
          onClick={handleStart}
          isLoading={createSession.isPending}
        >
          Start Checkout
        </Button>
        {createSession.isError && (
          <p className="mt-2 text-sm text-danger-600">
            Failed to create checkout session. Please try again.
          </p>
        )}
      </div>
    </div>
  )
}

function CheckoutSkeleton() {
  return (
    <div className="p-6">
      <div className="animate-pulse space-y-4">
        <div className="h-8 bg-gray-200 rounded w-3/4" />
        <div className="h-12 bg-gray-200 rounded" />
        <div className="h-12 bg-gray-200 rounded" />
        <div className="h-10 bg-gray-200 rounded w-1/3" />
      </div>
    </div>
  )
}
